import { useNavigate, useSearchParams } from 'react-router-dom'
import { SortingState } from '@tanstack/react-table'
import { isEqual } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { graphql } from 'app/generated/gql'
import { ScorecardRow as LocationsRow } from 'types/analytics'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { CreateNewScorecardModal } from 'app/components/scorecards/CreateNewScorecardModal'
import { ScorecardJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { gqlQuery } from 'app/lib/graphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { locationColumns, staffColumns } from 'app/table-columns/scorecards'
import { bg } from 'lib/background'
import { debug } from 'lib/logging'
import { useScorecardStore } from 'app/stores/StoreContext'

const SCORECARD_METRICS_QUERY = graphql(`
  query ScorecardMetrics(
    $dateRange: String!
    $grouping: String!
    $locations: [String!]
    $staff: [String!]
    $lookbackType: String
    $lookbackAmount: Int
  ) {
    scorecardMetrics(
      dateRange: $dateRange
      grouping: $grouping
      locations: $locations
      staff: $staff
      lookbackType: $lookbackType
      lookbackAmount: $lookbackAmount
    ) {
      bucket
      period
      leads
      MQLs
      appointments
      patients
      production
      scheduledProduction
      budget
      totalSpend
      ROI
      CPL
      CPQL
      CPA
      CAC
      scheduleRate
      leadsNeedsAction
      leadsInProgress
      leadsScheduled
      leadsClosedWon
      leadsClosedLost
      subRows {
        bucket
        period
        leads
        MQLs
        appointments
        patients
        production
        scheduledProduction
        budget
        totalSpend
        ROI
        CPL
        CPQL
        CPA
        CAC
        scheduleRate
        leadsNeedsAction
        leadsInProgress
        leadsScheduled
        leadsClosedWon
        leadsClosedLost
      }
    }
  }
`)

const getDefaultLocations = (activeScorecard?: ScorecardJson) => {
  if (!activeScorecard || activeScorecard.grouping !== `Location` || !activeScorecard.objectIds?.length) return []
  return activeScorecard.objectIds
}

const getDefaultStaff = (activeScorecard?: ScorecardJson) => {
  if (!activeScorecard || activeScorecard.grouping !== `Staff` || !activeScorecard.objectIds?.length) return []
  return activeScorecard.objectIds
}

const getDefaultDateRange = (activeScorecard?: ScorecardJson): NonNullable<ScorecardJson[`dateRange`]> => {
  return activeScorecard?.dateRange ?? `this_month`
}

const getDefaultLookbackType = (activeScorecard?: ScorecardJson) => {
  return activeScorecard?.lookbackType ?? `trailing_periods`
}

const getDefaultLookbackAmount = (activeScorecard?: ScorecardJson) => {
  return activeScorecard?.lookbackAmount ?? 3
}

const getDefaultColumnVisibility = (activeScorecard?: ScorecardJson) => {
  return activeScorecard?.columnVisibility ?? {}
}

const getDefaultColumnOrder = (activeScorecard?: ScorecardJson) => {
  return activeScorecard?.columnOrder ? [...activeScorecard.columnOrder] : []
}

const getDefaultSort = (activeScorecard?: ScorecardJson) => {
  return activeScorecard?.sort
    ? [...activeScorecard.sort]
    : activeScorecard?.grouping === `Location`
      ? [{ desc: false, id: `Location` }]
      : [{ desc: false, id: `Staff` }]
}

export type UseScorecardsReturn = {
  activeScorecard: ScorecardJson | undefined
  locations: string[]
  setLocations: React.Dispatch<React.SetStateAction<string[]>>
  staff: string[]
  setStaff: React.Dispatch<React.SetStateAction<string[]>>
  dateRange: NonNullable<ScorecardJson[`dateRange`]>
  lookbackType: string
  setLookbackType: React.Dispatch<React.SetStateAction<string>>
  lookbackAmount: number
  setLookbackAmount: React.Dispatch<React.SetStateAction<number>>
  rows: LocationsRow[] | undefined
  columnVisibility: { [k: string]: boolean }
  columnOrder: string[]
  sort: SortingState
  isLoading: boolean
  savable: boolean
  columns: ReturnType<typeof locationColumns> | ReturnType<typeof staffColumns> | []
  handleScorecardCreatedOrUpdated: (id: string) => void
  onCreateScorecardClick: () => void
  onSaveChangesClick: () => Promise<void>
  handleColumnVisibilityChange: (visibility: { [k: string]: boolean }) => void
  handleColumnOrderChange: (order: string[]) => void
  handleSortChange: (sort: SortingState) => void
  resetToDefaults: () => void
  handleDateRangeChange: (newDateRange: NonNullable<ScorecardJson[`dateRange`]>) => void
}

export function useScorecards(): UseScorecardsReturn {
  const [searchParams] = useSearchParams()
  const store = useScorecardStore()
  const [activeScorecard, setActiveScorecard] = useState<ScorecardJson>()

  useEffect(() => {
    const tab = searchParams.get(`tab`)
    store.fetchList({ search: tab ? `tab:${tab}` : undefined }).then(() => {
      // activeScorecard is the first hit or the one matching the tab
      const hits = store.hits as unknown as ScorecardJson[]
      const match = tab ? hits.find((s) => s.id === tab) : hits[0]
      setActiveScorecard(match)
    })
  }, [searchParams.get(`tab`)])

  const [locations, setLocations] = useState<string[]>([])
  const [staff, setStaff] = useState<string[]>([])
  const [dateRange, setDateRange] = useState<NonNullable<ScorecardJson[`dateRange`]>>(`this_month`)
  const [lookbackType, setLookbackType] = useState<string>(`trailing_periods`)
  const [lookbackAmount, setLookbackAmount] = useState<number>(3)
  const [rows, setRows] = useState<LocationsRow[]>()
  const [columnVisibility, setColumnVisibility] = useState<{ [k: string]: boolean }>(
    getDefaultColumnVisibility(activeScorecard),
  )
  const [columnOrder, setColumnOrder] = useState<string[]>(() => getDefaultColumnOrder(activeScorecard))
  const [sort, setSort] = useState<SortingState>(getDefaultSort(activeScorecard))
  const [confirm] = useConfirmation()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [savable, setSavable] = useState<boolean>(false)
  const navigate = useNavigate()

  const handleScorecardCreatedOrUpdated = (id: string) => {
    navigate({
      pathname: `/scorecards`,
      search: `?tab=${id}`,
    })
  }

  const columns = useMemo(() => {
    if (activeScorecard?.grouping === `Location`) {
      return locationColumns(dateRange)
    }
    if (activeScorecard?.grouping === `Staff`) {
      return staffColumns(dateRange)
    }
    return []
  }, [activeScorecard, dateRange])

  const onCreateScorecardClick = () => {
    showModal(<CreateNewScorecardModal onScorecardCreatedOrUpdated={(id) => handleScorecardCreatedOrUpdated(id)} />, {
      title: `Create New Scorecard`,
      isFullScreen: false,
      overflowHidden: false,
      removePadding: true,
      useRadix: true,
    })
  }

  const onSaveChangesClick = async () => {
    return confirm({
      title: `Save Changes`,
      description: `Are you sure you want to save changes to this scorecard?`,
      variant: `info`,
    }).then(async () => {
      const id = activeScorecard?.id
      if (!id) return
      const result = await store.update(id, {
        dateRange,
        lookbackType,
        lookbackAmount,
        objectIds: activeScorecard?.grouping === `Location` ? locations : staff,
        columnVisibility,
        columnOrder,
        sort: sort.length ? sort : undefined,
      })
      if (!result.error) {
        notifySuccess(`Changes saved successfully`)
      } else {
        notifyError(`Failed to save changes`)
      }
    })
  }

  const handleColumnVisibilityChange = (visibility: { [k: string]: boolean }) => {
    setColumnVisibility(visibility)
  }

  const handleColumnOrderChange = (order: string[]) => {
    setColumnOrder(order)
  }

  const handleSortChange = (sort: SortingState) => {
    setSort(sort)
  }

  const resetToDefaults = () => {
    setLocations(getDefaultLocations(activeScorecard))
    setStaff(getDefaultStaff(activeScorecard))
    setDateRange(getDefaultDateRange(activeScorecard))
    setLookbackType(getDefaultLookbackType(activeScorecard))
    setLookbackAmount(getDefaultLookbackAmount(activeScorecard))
    setColumnVisibility(getDefaultColumnVisibility(activeScorecard))
    setColumnOrder(getDefaultColumnOrder(activeScorecard))
    setSort(getDefaultSort(activeScorecard))
  }

  const handleDateRangeChange = (newDateRange: NonNullable<ScorecardJson[`dateRange`]>) => {
    setDateRange(newDateRange)
    setLookbackAmount(3)
    if ([`this_year`, `last_year`, `last_30_days`, `last_90_days`].includes(newDateRange)) {
      setLookbackType(`trailing_periods`)
    }
  }

  useEffect(() => {
    if (
      dateRange !== getDefaultDateRange(activeScorecard) ||
      lookbackType !== getDefaultLookbackType(activeScorecard) ||
      lookbackAmount !== getDefaultLookbackAmount(activeScorecard) ||
      !isEqual(locations, getDefaultLocations(activeScorecard)) ||
      !isEqual(staff, getDefaultStaff(activeScorecard)) ||
      !isEqual(columnVisibility, getDefaultColumnVisibility(activeScorecard)) ||
      !isEqual(columnOrder, getDefaultColumnOrder(activeScorecard)) ||
      !isEqual(sort, getDefaultSort(activeScorecard))
    ) {
      setSavable(true)
    } else {
      setSavable(false)
    }
  }, [dateRange, lookbackType, lookbackAmount, locations, staff, columnVisibility, columnOrder, sort])

  useEffect(() => {
    if (activeScorecard) {
      setLocations(getDefaultLocations(activeScorecard))
      setStaff(getDefaultStaff(activeScorecard))
      setDateRange(getDefaultDateRange(activeScorecard))
      setLookbackType(getDefaultLookbackType(activeScorecard))
      setLookbackAmount(getDefaultLookbackAmount(activeScorecard))
      setColumnVisibility(getDefaultColumnVisibility(activeScorecard))
      setColumnOrder(getDefaultColumnOrder(activeScorecard))
      setSort(getDefaultSort(activeScorecard))
      setIsLoading(true)
    }
  }, [activeScorecard])

  useEffect(() => {
    if (activeScorecard) {
      setIsLoading(true)
      bg(async () => {
        const result = await gqlQuery<{ scorecardMetrics: LocationsRow[] }>(SCORECARD_METRICS_QUERY, {
          dateRange,
          grouping: activeScorecard.grouping,
          locations: locations.length ? locations : undefined,
          staff: staff.length ? staff : undefined,
          lookbackType,
          lookbackAmount,
        })
        setRows(result.data?.scorecardMetrics)
        debug(`scorecards`, `metrics`, result.data?.scorecardMetrics)
        setIsLoading(false)
      })
    }
  }, [dateRange, lookbackType, lookbackAmount, locations, staff])

  return {
    activeScorecard,
    locations,
    setLocations,
    staff,
    setStaff,
    dateRange,
    lookbackType,
    setLookbackType,
    lookbackAmount,
    setLookbackAmount,
    rows,
    columnVisibility,
    columnOrder,
    sort,
    isLoading,
    savable,
    columns,
    handleScorecardCreatedOrUpdated,
    onCreateScorecardClick,
    onSaveChangesClick,
    handleColumnVisibilityChange,
    handleColumnOrderChange,
    handleSortChange,
    resetToDefaults,
    handleDateRangeChange,
  }
}
